"use strict";
/**
 * Common Utility Functions
 *
 * Reusable helper functions used across AI Hub Admin components
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.normalizeElementId = exports.getErrorMessage = exports.formatDateTime = exports.generateUniqueId = exports.deepClone = void 0;
/**
 * Keys whose presence in a JSON-deserialized payload indicates a
 * prototype-pollution attempt or an accidental shape collision. Always
 * stripped during deep cloning.
 */
var FORBIDDEN_OBJECT_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
/**
 * Deep clone an object.
 *
 * Prefers the structured-clone algorithm (handles cycles and rejects
 * functions). Falls back to a JSON round-trip with a reviver that strips
 * forbidden prototype-pollution keys. Functions, `Date`, `Map`, `Set` and
 * other non-JSON values are silently dropped on the fallback path; callers
 * must not rely on these surviving the clone.
 *
 * @template T - Type of object to clone
 * @param obj - Object to deep clone
 * @returns Deep cloned copy of the object
 */
exports.deepClone = function (obj) {
    if (typeof globalThis.structuredClone === 'function') {
        try {
            return globalThis.structuredClone(obj);
        }
        catch (error) {
            // Fall through to JSON-based clone for non-cloneable shapes.
        }
    }
    return JSON.parse(JSON.stringify(obj), function (key, value) {
        if (FORBIDDEN_OBJECT_KEYS.has(key)) {
            return undefined;
        }
        return value;
    });
};
/**
 * Generate a unique ID
 *
 * @returns Unique string identifier
 */
exports.generateUniqueId = function () {
    return Date.now() + "-" + Math.random();
};
/**
 * Format a date/time string for display
 *
 * @param dateString - ISO date string or Date object
 * @returns Formatted date string (e.g., "Nov 13, 2023, 2:30 PM")
 */
exports.formatDateTime = function (dateString) {
    var date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
};
/**
 * Extract error message from unknown error type
 *
 * @param error - Unknown error object
 * @param defaultMessage - Default message if error is not an Error instance
 * @returns Error message string
 */
exports.getErrorMessage = function (error, defaultMessage) {
    if (defaultMessage === void 0) { defaultMessage = 'An unexpected error occurred'; }
    return error instanceof Error ? error.message : defaultMessage;
};
/**
 * Normalize element IDs to a consistent lowercase form.
 * Supports values like "tech.azure-openai" and "tech:azure-openai".
 */
exports.normalizeElementId = function (rawId) {
    var value = rawId.trim().toLowerCase();
    var firstDot = value.indexOf('.');
    if (firstDot < 0) {
        return value;
    }
    var leftPart = value.slice(0, firstDot);
    var rightPart = value.slice(firstDot + 1);
    var leftLooksLikeCategory = leftPart.indexOf(':') < 0;
    return leftLooksLikeCategory ? rightPart : value;
};
